import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Spinner } from '@/components/ui/spinner';
import { Button } from '@/components/ui/button';
import { isInIframe, openPopoutWindow } from '@/utils/iframeUtils';
import { devLog } from '@/utils/devLog';

const AuthCallback: React.FC = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState<'processing' | 'timeout' | 'error'>('processing');
  const [errorMessage, setErrorMessage] = useState<string>('');

  useEffect(() => {
    const handleAuthCallback = async () => {
      try {
        devLog('🔄 AuthCallback: Starting auth callback processing...');
        
        // Set a timeout to prevent infinite loading
        const timeoutId = setTimeout(() => {
          devLog('⏰ AuthCallback: Timeout reached, redirecting...');
          setStatus('timeout');
        }, 8000); // 8 second timeout (matches AuthContext)

        // Get the URL parameters
        const urlParams = new URLSearchParams(window.location.search);
        const code = urlParams.get('code');
        const error = urlParams.get('error');
        const errorDescription = urlParams.get('error_description');

        devLog('AuthCallback params:', { code: !!code, error, errorDescription });

        // Handle OAuth errors first
        if (error) {
          clearTimeout(timeoutId);
          console.error('OAuth error:', error, errorDescription);
          
          // If we're in an iframe and this is a Google auth error, suggest popup
          if (isInIframe() && error.includes('access_denied')) {
            setErrorMessage('Google authentication requires opening in a new window when using the preview.');
            setStatus('error');
            return;
          }
          
          setErrorMessage(errorDescription || 'Authentication failed');
          setStatus('error');
          return;
        }

        // Exchange the OAuth code for a session
        if (code) {
          devLog('🔑 AuthCallback: Exchanging code for session...');
          const { data, error: exchangeError } = await supabase.auth.exchangeCodeForSession(code);
          
          clearTimeout(timeoutId);
          
          if (exchangeError) {
            console.error('Error exchanging code for session:', exchangeError);
            if (isInIframe()) {
              setErrorMessage('Authentication failed inside the preview. This can happen due to browser security settings. Please return to the app and try again, which should open auth in a new window.');
            } else {
              setErrorMessage('Failed to complete authentication. Please try again.');
            }
            setStatus('error');
            return;
          }

          if (data.session) {
            devLog('✅ AuthCallback: OAuth session established successfully');
            navigate('/', { replace: true });
          } else {
            devLog('❌ AuthCallback: No session after code exchange');
            if (isInIframe()) {
              setErrorMessage('Authentication incomplete inside the preview. Please return to the app and try again.');
            } else {
              setErrorMessage('Authentication incomplete. Please try again.');
            }
            setStatus('error');
          }
        } else {
          clearTimeout(timeoutId);
          // No code parameter, check for existing session
          const { data, error: sessionError } = await supabase.auth.getSession();
          
          if (sessionError) {
            console.error('Auth callback session error:', sessionError);
            setErrorMessage('Session verification failed');
            setStatus('error');
            return;
          }

          if (data.session) {
            devLog('✅ AuthCallback: Existing session found, redirecting...');
            navigate('/', { replace: true });
          } else {
            devLog('❌ AuthCallback: No session found, redirecting to login...');
            navigate('/', { replace: true });
          }
        }
      } catch (error) {
        console.error('Error handling auth callback:', error);
        setErrorMessage('Unexpected authentication error');
        setStatus('error');
      }
    };

    handleAuthCallback();
  }, [navigate]);

  const handleRetry = () => {
    navigate('/', { replace: true });
  };

  const handleOpenPopout = () => {
    openPopoutWindow();
    setTimeout(() => {
      navigate('/', { replace: true });
    }, 1000);
  };

  if (status === 'timeout') {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="text-orange-500 mb-4">
            <svg className="h-12 w-12 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-800 mb-2">Authentication Taking Too Long</h2>
          <p className="text-gray-600 mb-4">
            The authentication process is taking longer than expected.
          </p>
          <div className="space-y-2">
            <Button onClick={handleRetry} className="w-full">
              Return to App
            </Button>
            {isInIframe() && (
              <Button onClick={handleOpenPopout} variant="outline" className="w-full">
                Open in New Window
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="text-red-500 mb-4">
            <svg className="h-12 w-12 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-800 mb-2">Authentication Error</h2>
          <p className="text-gray-600 mb-4">{errorMessage}</p>
          <div className="space-y-2">
            <Button onClick={handleRetry} className="w-full">
              Try Again
            </Button>
            {isInIframe() && (
              <Button onClick={handleOpenPopout} variant="outline" className="w-full">
                Open in New Window
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="text-center">
        <Spinner className="h-8 w-8 text-[#734E85] mx-auto mb-4" />
        <p className="text-gray-600">Completing sign in...</p>
        <p className="text-sm text-gray-400 mt-2">This should only take a moment</p>
      </div>
    </div>
  );
};

export default AuthCallback;
